import React, { useEffect } from 'react';
import { t } from '@/locales/strings';
import { useModalA11y } from '@/hooks/useModalA11y';
import { DUR, EASE } from '@/design/tokens';

/**
 * StreakMilestoneOverlay — one-time celebration when a user hits a
 * milestone streak (7 / 30 / 100 / 365 days). Renders over the home
 * screen with a soft sage-to-rose gradient, a large milestone
 * number, a localised "you've earned this" line, and two actions:
 * "Share" (opens the existing StreakShareModal) and "Keep going"
 * (dismisses).
 *
 * The parent owns the open/close state and whether to trigger
 * sharing — this component is presentation only. See
 * useStreakMilestone() below for the detection logic + localStorage
 * gating so each milestone fires exactly once per user.
 */
export default function StreakMilestoneOverlay({ milestone, lang = 'en', onShare, onClose }) {
  const dialogRef = useModalA11y({ open: !!milestone, onClose });
  // Gentle confirm-ding + haptic burst when the overlay first mounts.
  // Sound is via the existing sound.js helper (respects the user's
  // mute preference). Haptic is a distinct three-pulse pattern so
  // the moment is felt as a celebration, not a notification.
  useEffect(() => {
    if (!milestone) return;
    let cancelled = false;
    import('@/utils/sound').then(mod => {
      if (!cancelled) mod.playConfirmDing?.();
    }).catch(() => {});
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate([40, 80, 40, 80, 60]);
      }
    } catch {}
    return () => { cancelled = true; };
  }, [milestone]);

  if (!milestone) return null;

  const title =
      milestone === 7   ? t('milestone_7_title', lang)
    : milestone === 30  ? t('milestone_30_title', lang)
    : milestone === 100 ? t('milestone_100_title', lang)
    : milestone === 365 ? t('milestone_365_title', lang)
    : t('milestone_generic_title', lang).replace('{n}', String(milestone));

  const subtitle =
      milestone === 7   ? t('milestone_7_sub', lang)
    : milestone === 30  ? t('milestone_30_sub', lang)
    : milestone === 100 ? t('milestone_100_sub', lang)
    : milestone === 365 ? t('milestone_365_sub', lang)
    : t('milestone_generic_sub', lang);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="milestone-title"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 650,
        background: 'rgba(28, 14, 22, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        paddingTop: 'max(20px, env(safe-area-inset-top))',
        paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
        paddingLeft: 'max(20px, env(safe-area-inset-left))',
        paddingRight: 'max(20px, env(safe-area-inset-right))',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: `bb-milestone-fade ${DUR.med}ms ${EASE.standard}`,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 360,
          background:
            'linear-gradient(180deg, #fff 0%, var(--primary-pale, #fbeae7) 55%, color-mix(in srgb, var(--primary-pale, #fbeae7) 75%, var(--primary-light, #e3a193)) 100%)',
          borderRadius: 28,
          padding: '36px 28px 24px',
          boxShadow: '0 30px 80px -20px rgba(45,31,38,.5)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          animation: `bb-milestone-pop ${DUR.long}ms ${EASE.spring}`,
        }}
      >
        {/* Decorative aura */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse 60% 40% at 50% 40%, rgba(255,214,203,0.55) 0%, transparent 70%)',
        }} />

        <div style={{
          font: '600 10px/1 var(--font-ui, Manrope)',
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'var(--primary-dark, #a85c4b)',
          zIndex: 1,
        }}>
          {t('milestone_eyebrow', lang)}
        </div>

        <div style={{
          font: '400 108px/1 var(--font-title, Fraunces), serif',
          fontVariationSettings: '"opsz" 144',
          letterSpacing: '-0.04em',
          color: 'var(--primary-dark, #a85c4b)',
          zIndex: 1,
        }}>
          {milestone}
        </div>

        <div id="milestone-title" style={{
          font: '500 18px/1.2 var(--font-title, Fraunces), serif',
          fontStyle: 'italic',
          color: 'var(--ink, #2d1f26)',
          zIndex: 1,
          textWrap: 'balance',
        }}>
          {title}
        </div>

        <div style={{
          font: '400 14px/1.4 var(--font-body, Manrope)',
          color: 'var(--ink-dim, #6b5560)',
          maxWidth: 260,
          zIndex: 1,
          textWrap: 'balance',
        }}>
          {subtitle}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8, width: '100%', zIndex: 1 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              minHeight: 48,
              borderRadius: 999,
              border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 12%, transparent)',
              background: 'rgba(255,255,255,0.6)',
              color: 'var(--ink, #2d1f26)',
              font: '600 14px/1 var(--font-ui, Manrope)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {t('milestone_keep_going', lang)}
          </button>
          <button
            type="button"
            onClick={onShare}
            style={{
              flex: 1.3,
              minHeight: 48,
              borderRadius: 999,
              border: 'none',
              background:
                'linear-gradient(180deg, var(--primary-light, #e3a193) 0%, var(--primary, #c97a68) 100%)',
              color: '#fff',
              font: '600 14px/1 var(--font-ui, Manrope)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 10px 24px -10px color-mix(in srgb, var(--primary, #c97a68) 60%, transparent)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 12v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" />
              <path d="M16 6l-4-4-4 4" />
              <path d="M12 2v14" />
            </svg>
            <span>{t('milestone_share_btn', lang)}</span>
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bb-milestone-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes bb-milestone-pop {
          0%   { transform: scale(0.86) translateY(10px); opacity: 0; }
          60%  { transform: scale(1.02) translateY(0);     opacity: 1; }
          100% { transform: scale(1)    translateY(0);     opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ── useStreakMilestone hook ──────────────────────────────────────
// Returns { milestone, dismiss } — `milestone` is the day-count if a
// celebration should fire right now, else null. Tracks seen
// milestones in localStorage so each one fires exactly once per
// user.

const MILESTONES = [7, 14, 30, 60, 100, 200, 365];
const STORAGE_KEY = 'bb_streak_milestones_seen';

function readSeen() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function writeSeen(arr) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); } catch {}
}

export function useStreakMilestone(streakDays) {
  const [milestone, setMilestone] = React.useState(null);

  React.useEffect(() => {
    if (!streakDays || streakDays < 1) return;
    const seen = new Set(readSeen());
    // Fire the *highest* unseen milestone that's ≤ current streak.
    // Covers the common path (user crosses 7, 30, etc. as it
    // happens) and the hiatus-return path (user reopens the app
    // with a fresh streak=100, never saw 7/14/30/60 — we don't
    // want to chain-fire every one of them).
    //
    // When a hiatus-return fires the highest, we silently mark
    // every lower unseen milestone as seen too so the user gets
    // a single ceremonial moment at the highest tier and isn't
    // held hostage by five consecutive overlays the next time
    // she visits.
    for (let i = MILESTONES.length - 1; i >= 0; i--) {
      const m = MILESTONES[i];
      if (m <= streakDays && !seen.has(m)) {
        setMilestone(m);
        const belowUnseen = MILESTONES.filter(x => x < m && !seen.has(x));
        if (belowUnseen.length) {
          for (const lower of belowUnseen) seen.add(lower);
          writeSeen(Array.from(seen));
        }
        break;
      }
    }
  }, [streakDays]);

  const dismiss = React.useCallback(() => {
    if (milestone == null) return;
    const seen = new Set(readSeen());
    seen.add(milestone);
    writeSeen(Array.from(seen));
    setMilestone(null);
  }, [milestone]);

  return { milestone, dismiss };
}
